/**
 * plugins/translatr/ContactDetailsPlugin.ts - Contact Details Extraction Plugin for Translatr
 * 
 * CHANGE HISTORY:
 * - 251109_0812 [pending] danielbeer: Migrated from Interfacr to Pluggr
 * - 251103_1115 [pending] danielbeer: Contact extraction plugin using Distillr
 * 
 * Pluggr plugin that provides contact information extraction for Translatr.
 * Extracts contact information from any text and converts to vCard JSON format.
 */

import type { PluginManifest, PluginContext } from '../../PluggrTypes';

/**
 * Contact data structure
 */
export interface ContactData {
  name?: string;
  givenName?: string;
  familyName?: string;
  organization?: string;
  title?: string;
  emails?: string[];
  phones?: string[];
  addresses?: Array<{
    type?: string;
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>;
  urls?: string[];
  social?: Record<string, string>;
  birthday?: string;
  notes?: string;
  raw?: Record<string, any>;
}

/**
 * Translatr Format Plugin interface
 */
export interface TranslatrFormatPlugin {
  id: string;
  name: string;
  description: string;
  format: string;
  icon?: string;
  canParse: (input: string) => boolean;
  parse: (input: string) => Promise<ContactData>;
  serialize: (data: ContactData) => Promise<string>;
  detect?: (input: string) => number;
  syntaxLanguage?: string;
  options?: Record<string, any>;
}

/**
 * Plugin manifest
 */
export const manifest: PluginManifest = {
  id: 'translatr-contact-details',
  name: 'Contact Details Extractor',
  version: '1.0.0',
  description: 'Extract contact information from text and convert to vCard JSON format',
  author: 'OpCentral',
  main: 'ContactDetailsPlugin.ts',
  icon: '👤',
  category: 'translatr',
  tags: ['translatr', 'contact', 'vcard', 'extraction'],
  features: ['contact-extraction', 'vcard-generation'],
  permissions: {
    files: 'none',
    network: 'none',
    system: 'none'
  }
};

/**
 * Contact Details Plugin for Translatr
 */
export const ContactDetailsPlugin: TranslatrFormatPlugin = {
  id: 'contact-details',
  name: 'Contact Details',
  description: 'Extract contact information from text and convert to vCard JSON',
  format: 'contact',
  icon: '👤',
  
  /**
   * Can parse any text content
   */
  canParse: (input: string): boolean => {
    return input.trim().length > 0;
  },
  
  /**
   * Parse input and extract contact information
   */
  parse: async (input: string): Promise<ContactData> => {
    const contact: ContactData = {
      emails: [],
      phones: [],
      addresses: [],
      urls: [],
      social: {},
      raw: {}
    };
    
    // Extract emails
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const emails = input.match(emailRegex);
    if (emails) {
      contact.emails = [...new Set(emails)];
    }
    
    // Extract phone numbers
    const phoneRegex = /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;
    const phones = input.match(phoneRegex);
    if (phones) {
      contact.phones = [...new Set(phones.map(p => p.trim()))];
    }
    
    // Extract URLs
    const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
    const urls = input.match(urlRegex);
    if (urls) {
      contact.urls = [...new Set(urls)];
    }
    
    // Extract name
    const namePatterns = [
      /Name:\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i,
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/m,
      /Contact:\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i
    ];
    
    for (const pattern of namePatterns) {
      const match = input.match(pattern);
      if (match) {
        contact.name = match[1].trim();
        const nameParts = contact.name.split(/\s+/);
        if (nameParts.length >= 2) {
          contact.givenName = nameParts[0];
          contact.familyName = nameParts.slice(1).join(' ');
        }
        break;
      }
    }
    
    // Extract organization
    const orgPatterns = [
      /(?:Company|Organization|Org):\s*([^\n]+)/i,
      /(?:at|@)\s+([A-Z][a-zA-Z\s&]+(?:Inc|LLC|Ltd|Corp|Company))/
    ];
    
    for (const pattern of orgPatterns) {
      const match = input.match(pattern);
      if (match) {
        contact.organization = match[1].trim();
        break;
      }
    }
    
    // Extract title/position
    const titlePatterns = [
      /(?:Title|Position|Role):\s*([^\n]+)/i,
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+at\s+/m
    ];
    
    for (const pattern of titlePatterns) {
      const match = input.match(pattern);
      if (match) {
        contact.title = match[1].trim();
        break;
      }
    }
    
    // Extract address
    const addressPattern = /(\d+\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Way)(?:,?\s+(?:Apt|Suite|Unit|#)\s*\d+)?(?:,?\s+[A-Za-z\s]+,?\s+[A-Z]{2}\s+\d{5}(?:-\d{4})?)?)/i;
    const addressMatch = input.match(addressPattern);
    if (addressMatch) {
      const addressText = addressMatch[1];
      const parts = addressText.split(',').map(p => p.trim());
      
      const address: any = {
        type: 'work'
      };
      
      if (parts.length >= 1) address.street = parts[0];
      if (parts.length >= 2) address.city = parts[1];
      if (parts.length >= 3) {
        const stateZip = parts[2].match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/);
        if (stateZip) {
          address.state = stateZip[1];
          address.postalCode = stateZip[2];
        }
      }
      
      contact.addresses = [address];
    }
    
    // Extract social media
    const socialPatterns = {
      linkedin: /linkedin\.com\/in\/([a-zA-Z0-9-]+)/i,
      twitter: /twitter\.com\/([a-zA-Z0-9_]+)/i,
      github: /github\.com\/([a-zA-Z0-9-]+)/i,
      facebook: /facebook\.com\/([a-zA-Z0-9.]+)/i
    };
    
    for (const [platform, pattern] of Object.entries(socialPatterns)) {
      const match = input.match(pattern);
      if (match) {
        contact.social![platform] = match[1];
      }
    }
    
    // Store raw input
    contact.raw = {
      originalText: input,
      extractedAt: new Date().toISOString()
    };
    
    return contact;
  },
  
  /**
   * Serialize contact data to vCard JSON format
   */
  serialize: async (data: ContactData): Promise<string> => {
    const vcard: any = {
      version: '4.0',
      fn: data.name || 'Unknown',
      n: {
        familyName: data.familyName || '',
        givenName: data.givenName || '',
        additionalName: '',
        honorificPrefix: '',
        honorificSuffix: ''
      }
    };
    
    if (data.emails && data.emails.length > 0) {
      vcard.email = data.emails.map((email, idx) => ({
        type: idx === 0 ? 'work' : 'home',
        value: email
      }));
    }
    
    if (data.phones && data.phones.length > 0) {
      vcard.tel = data.phones.map((phone, idx) => ({
        type: idx === 0 ? 'work' : 'home',
        value: phone
      }));
    }
    
    if (data.addresses && data.addresses.length > 0) {
      vcard.adr = data.addresses.map(addr => ({
        type: addr.type || 'work',
        poBox: '',
        extendedAddress: '',
        streetAddress: addr.street || '',
        locality: addr.city || '',
        region: addr.state || '',
        postalCode: addr.postalCode || '',
        countryName: addr.country || ''
      }));
    }
    
    if (data.organization) {
      vcard.org = data.organization;
    }
    
    if (data.title) {
      vcard.title = data.title;
    }
    
    if (data.urls && data.urls.length > 0) {
      vcard.url = data.urls.map(url => ({
        type: 'work',
        value: url
      }));
    }
    
    if (data.social && Object.keys(data.social).length > 0) {
      vcard.socialProfile = Object.entries(data.social).map(([platform, username]) => ({
        type: platform,
        value: username
      }));
    }
    
    if (data.birthday) {
      vcard.bday = data.birthday;
    }
    
    if (data.notes) {
      vcard.note = data.notes;
    }
    
    return JSON.stringify(vcard, null, 2);
  },
  
  /**
   * Detect if input contains contact information
   */
  detect: (input: string): number => {
    let confidence = 0;
    
    if (/@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(input)) {
      confidence += 30;
    }
    
    if (/\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/.test(input)) {
      confidence += 25;
    }
    
    if (/[A-Z][a-z]+\s+[A-Z][a-z]+/.test(input)) {
      confidence += 20;
    }
    
    if (/\d+\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd)/.test(input)) {
      confidence += 15;
    }
    
    if (/https?:\/\//.test(input)) {
      confidence += 10;
    }
    
    return Math.min(confidence, 100);
  },
  
  syntaxLanguage: 'json',
  
  options: {
    prettyPrint: true,
    indentSize: 2
  }
};

/**
 * Plugin onload function
 */
export async function onload(context: PluginContext) {
  // Register the plugin with Translatr if available
  if (context.registerFunction) {
    context.registerFunction('translatr:register-plugin', () => ContactDetailsPlugin);
  }
  
  if (context.log) {
    context.log.info('Contact Details Plugin loaded for Translatr');
  }
}

/**
 * Plugin onunload function
 */
export async function onunload(context: PluginContext) {
  if (context.log) {
    context.log.info('Contact Details Plugin unloaded');
  }
}

export default {
  manifest,
  onload,
  onunload,
  plugin: ContactDetailsPlugin
};
